// Per-set LOAD math — what one LogSet did, in kg / seconds / impulse.
// Split out of sessionResult.ts (CO-10) when that file reached 496/500: this
// half is self-contained (its only inputs are a LogSet, the two catalog load
// facts, and a bodyweight) and has no idea sessions or aggregates exist, while
// the other half orchestrates them. effortCoverage consumes isCountedLogSet
// from here — set math, not session-aggregation logic.
//
// One misplaced comment was corrected in the move: the "Volume from a single
// LogSet" block described setVolumeKg but sat above holdEffectiveLoadKg, with
// a dangling half-sentence between them. Each comment now sits on the function
// it describes; no code changed.

import { dropsetSetTotals, type DropsetRound } from "./dropsetTotals.js";
import type { LogSet } from "./schemas/logSet.js";


// The ONLY two catalog facts the per-set load math reads. Split out of the
// lookup below so the load helpers declare exactly what they depend on, and
// so a caller holding a full catalog Exercise (whose `name` is LocalizedText,
// not a string) can feed them directly instead of inventing a lookup row.
export type SetLoadLookup = {
  // Percentage of bodyweight effectively moved per rep on kind=bodyweight
  // sets. Absent → 100 (full bw). See schemas/exercise.ts:bodyweightLoadPct
  // for the reference values.
  bodyweightLoadPct?: number;
  // Whether the exercise loads bodyweight — drives the effective-load of a
  // timed/isometric hold (a plank loads bw×pct; a purely-weighted hold
  // loads only its `extra`). Absent → treated as bodyweight for a timed
  // set (most isometrics are). (2026-06-09 isometric load model.)
  isBodyweight?: boolean;
};

// The effective load a timed/isometric hold puts on the working muscles:
// bodyweight × bodyweightLoadPct + any added load. A purely-weighted hold
// (catalog isBodyweight=false, e.g. a loaded carry with two dumbbells) loads
// only that added weight — so without it, the hold counts 0 volume and never
// reaches the muscle map.
export const holdEffectiveLoadKg = (
  set: LogSet,
  catalogEntry: SetLoadLookup | undefined,
  profileBodyweightKg: number,
): number => {
  // Absent isBodyweight → assume bodyweight (most isometrics are); the
  // catalog sets the flag + a real pct per exercise (isometric load model).
  const loadsBw = catalogEntry?.isBodyweight !== false;
  const pct = catalogEntry?.bodyweightLoadPct ?? 100;
  const bwPart = loadsBw ? (profileBodyweightKg * pct) / 100 : 0;
  // The timed logger + storedSetToDraftSet store added load in `weight`
  // (FocusPanelTimed's extra field), so prefer it; `extra` is the legacy/
  // importer fallback. Reading only `extra` made every weighted hold count 0.
  return bwPart + (set.weight ?? set.extra ?? 0);
};

// Per-set metrics. A timed/isometric hold contributes its EFFECTIVE LOAD
// (once, like one rep) to volume — NOT load × duration, which inflated
// absurdly with long holds (a 30-min hold once read as 33 tonnes of core;
// Antenore 2026-06-09 "36 tonnellate, non le avevo mai viste"). Duration's
// weight lives where it belongs: TUT (Σ seconds) + impulse (load × seconds,
// the physiologically-correct isometric metric). So volume stays familiar
// (weight × reps, with a small per-hold load on top), and a 60s vs 600s
// hold differ in TUT/impulse, not volume. A fail is a pure LABEL (AP-0p
// model, 2026-07-06): `reps` already holds the clean reps done, so a failed
// set counts exactly like any other — no exclusion.
//
// ⚠️ A CARDIO BOUT PRODUCES NEITHER, ON PURPOSE (Antenore, 2026-07-22: «lascia
// il cardio fuori»). `kind === "cardio"` has no volume — there is no weight ×
// reps to compute — and it is deliberately NOT given the timed branch's TUT
// either, so it leaves here with 0/0 and `countsFromMetrics` reads false. That
// makes a run invisible to setCount / hardSetCount and to effortCoverage alike.
//
// The reason is the household's data, not a taxonomy argument. Their cardio
// arrives almost entirely from the watch through Health Connect (Walking,
// Hiking), and a watch cannot know an RPE: those bouts would be permanently
// untagged. Counting them would drag the coverage ratio down for a reason that
// belongs to the DEVICE, not to the athlete — the same class of mistake the
// hardcoded effort-era would have made (see effortCoverage.ts). MX-5 still
// demands an effort on a hand-logged bout and stores it; it simply is not what
// these metrics measure.
//
// So: giving cardio a TUT here is NOT a small consistency fix. It moves the
// family's set counts, retroactively, across Progress and the coach panels.
// Pinned by "leaves a cardio bout OUT of the denominator" in
// effortCoverage.test.ts, because the exclusion is otherwise true only by the
// absence of a branch.
export const setMetrics = (
  set: LogSet,
  catalogEntry: SetLoadLookup | undefined,
  profileBodyweightKg: number,
): { volumeKg: number; tutSec: number; impulseKgS: number } => {
  if (set.kind === "timed") {
    const dur = set.duration ?? 0;
    if (dur <= 0) return { volumeKg: 0, tutSec: 0, impulseKgS: 0 };
    const load = holdEffectiveLoadKg(set, catalogEntry, profileBodyweightKg);
    return { volumeKg: load, tutSec: dur, impulseKgS: load * dur };
  }
  return {
    volumeKg: setVolumeKg(set, catalogEntry, profileBodyweightKg),
    tutSec: 0,
    impulseKgS: 0,
  };
};

// "Did this set produce real work?" — the ONE definition of a counted set.
// Volume OR time under tension; a weightless hold is still a real set even
// when its load is 0. Kept as a metrics-level predicate so the compute loop
// below reuses the metrics it already has instead of resolving them twice.
export const countsFromMetrics = (m: { volumeKg: number; tutSec: number }): boolean =>
  m.volumeKg > 0 || m.tutSec > 0;

// Public twin of the predicate, for callers that hold raw LogSets rather
// than a compute loop (effortCoverage). Shares countsFromMetrics with
// computeSessionResult so a coverage denominator can never disagree with
// ExerciseResult.setCount/hardSetCount — the drift this export exists to
// prevent.
//
// bodyweightKg only has to be POSITIVE, not accurate: a bodyweight set's
// volume scales linearly with it, so the boolean is invariant to its
// magnitude (the same property that makes hardSetCount bw-independent).
// That is what lets the coach read path call this without ever seeing the
// athlete's bodyweight, which pickCoachProfileFields deliberately withholds.
export const isCountedLogSet = (
  set: LogSet,
  catalogEntry: SetLoadLookup | undefined,
  bodyweightKg: number,
): boolean => countsFromMetrics(setMetrics(set, catalogEntry, bodyweightKg));

// Volume from a single LogSet, counting the top round AND every dropset/
// myorep chain step exactly once (via the shared dropsetSetTotals — same
// formula the live in-workout footer uses, so Progress and the logger
// agree). Weighted: `reps × weight × loadedSides`. Bodyweight: `reps ×
// (bw × pct/100 + extra)` where pct comes from the catalog entry (default
// 100) and bw is profileBodyweightKg. Returns 0 for non-weighted/non-
// bodyweight kinds, or when inputs are missing. Bodyweight sets without a
// profileBodyweightKg fall back to 0 so legacy callers stay compatible.
//
// ⚠️ It does NOT return 0 for a failed set, and must not: under the AP-0p
// model (2026-07-06) `fail` is a pure label and `reps` already holds the
// clean reps done, so a failed set counts like any other (see the block
// above holdEffectiveLoadKg, and `fail` in schemas/logSet.ts). This
// sentence used to say the opposite — the code never read `set.fail`, so
// the comment was describing a behaviour that has never existed. Pinned
// now by setMetrics.test.ts.
export const setVolumeKg = (
  set: LogSet,
  catalogEntry: SetLoadLookup | undefined,
  profileBodyweightKg: number,
): number => {
  if (set.kind !== "weighted" && set.kind !== "bodyweight") return 0;
  const isBw = set.kind === "bodyweight";
  const pct = catalogEntry?.bodyweightLoadPct ?? 100;
  const rounds: DropsetRound[] = [];
  // Top round = the parent set.
  if (isBw) {
    rounds.push({
      kind: "bodyweight",
      reps: set.reps ?? 0,
      extraKg: set.extra ?? 0,
      loadedSides: set.loadedSides,
    });
  } else {
    rounds.push({
      kind: "weighted",
      reps: set.reps ?? 0,
      weightKg: set.weight ?? null,
      loadedSides: set.loadedSides,
    });
  }
  // Chain steps. A myorep step persisted without a weight inherits the
  // top load inside dropsetSetTotals (null weightKg + variant=myorep).
  if (set.dropset) {
    for (const st of set.dropset.steps) {
      if (isBw) {
        rounds.push({
          kind: "bodyweight",
          reps: st.reps,
          extraKg: st.extra ?? 0,
          loadedSides: set.loadedSides,
        });
      } else {
        rounds.push({
          kind: "weighted",
          reps: st.reps,
          // null (no stored weight) is intentional: dropsetSetTotals
          // applies the myorep "same load as top" inherit rule to it.
          // Do NOT coerce to 0 — that would zero a myorep cluster.
          weightKg: st.weight ?? null,
          loadedSides: set.loadedSides,
        });
      }
    }
  }
  const variant = set.dropset?.variant ?? "classical";
  return dropsetSetTotals(variant, rounds, {
    bodyweightKg: profileBodyweightKg,
    bodyweightLoadPct: pct,
  }).volumeKg;
};
